import { useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { IconDotsVertical } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { Venue } from '../../types';
import { useDeleteVenue } from '../../hooks/useVenueManagement';

interface VenueActionsProps {
  venue: Venue;
}

export function VenueActions({ venue }: VenueActionsProps) {
  const { hasPermission } = useAuth();
  const deleteVenue = useDeleteVenue();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleEdit = () => {
    toast.info('Edit functionality is not implemented yet');
  };

  const handleDelete = () => {
    deleteVenue.mutate(venue.id);
    setShowDeleteDialog(false);
  };

  // Check if user has any relevant permissions
  const canUpdate = hasPermission('venue_management:update');
  const canDelete = hasPermission('venue_management:delete');

  // Don't render anything if user has no permissions
  if (!canUpdate && !canDelete) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="data-[state=open]:bg-muted text-muted-foreground flex size-8 ml-auto"
            size="icon"
            data-testid={`venue-actions-${venue.id}`}
            disabled={deleteVenue.isPending}
          >
            <IconDotsVertical />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {canUpdate && (
            <DropdownMenuItem
              onClick={handleEdit}
              data-testid={`edit-venue-${venue.id}`}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive focus:text-destructive"
                data-testid={`delete-venue-${venue.id}`}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-venue-dialog">
          <DialogHeader>
            <DialogTitle>Delete Venue</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this venue? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <p className="text-sm font-medium">{venue.name}</p>
            <p className="text-sm text-muted-foreground">
              {venue.formatted_address}
            </p>
            {venue.organization && (
              <p className="text-sm text-muted-foreground">
                Organization: {venue.organization.name}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteVenue.isPending}
              data-testid="cancel-delete-venue"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteVenue.isPending}
              data-testid="confirm-delete-venue"
            >
              {deleteVenue.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}